import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { APPLICATION_ONE, COMPLETE, CURRENT, SPOUSE_COVER, UPCOMING } from "@ipru-common/constants/SiteConstants";
import { useGlobalContext } from "./GlobalContext";
import { updateUserInput } from "@ipru-common/utils/UserInputMapper/CommonUserInput";
import { AppFormContext } from "shared-library";
import { isIwishAndDependent, isProductIWish } from "@ipru-common/utils/CommonFunctions";
import { PAGE_STEPS } from "@ipru-common/constants/products";
import { readQueryParams } from "@ipru-common/utils/CommonFunctions";
import usePostFeedBackService from "@ipru-common/libs/usePostFeedbackService";

// const LIFE_ASSURED_DETAILS = 'life_assured_details';
// const HEALTH_DETAIL = 'health_details';
// const REVIEW_APPLICATEION = 'payment';

export const AppFormProvider = ({ children }) => {
  const { setGlobal, global: globalValues } = useGlobalContext();

  const currentApplication = globalValues?.currentApplication;

  const isTdPersonaJourney = globalValues?.isTdPersonaJourney;

  const getTdSteps = () => ({
    Personal_Details: CURRENT,
    Health_Details: UPCOMING,
    Pre_Confirmation: UPCOMING,
  });

  const getNormalSteps = () => ({
    ...(isIwishAndDependent(globalValues?.calculatorFormData) ? { your_details: CURRENT } : {}),
    life_assured_details: isIwishAndDependent(globalValues?.calculatorFormData) ? UPCOMING : CURRENT,
    health_details: UPCOMING,
    ...(isProductIWish() ? { payout_details: UPCOMING } : {}),
    payment: UPCOMING,
  });
  const [redirectTo, setRedirectTo] = useState(globalValues?.appFormData?.redirectTo);

  const [steps, setSteps] = useState(() =>
    isTdPersonaJourney ? getTdSteps() : globalValues?.appFormData?.currentFormStep || getNormalSteps()
  );

  const formRef = useRef(null);

  const [lifeAssuredTab, setLifeAssuredTab] = useState(globalValues?.appFormData?.lifeAssuredTab ?? 0);
  const [proposerTab, setProposerTab] = useState(0);
  const queryParams = readQueryParams();
  const isResumeJourney = queryParams.get("applicationState") === "resume";
  const [healthDetailTab, setHealthDetailTab] = useState(globalValues?.appFormData?.healthDetailTab ?? 0);
  const [payoutTab, setPayoutTab] = useState(globalValues?.appFormData?.payoutTab ?? 0);

  const changeProposerTab = (index) => {
    setProposerTab(index);
  };
  const changeLifeAssuredTab = (index) => {
    setLifeAssuredTab(index);
  };

  const changeHealthDetailTab = (index) => {
    setHealthDetailTab(index);
    updateAppFormData({
      healthDetailTab: index,
    });
  };

  const { submitFeedback } = usePostFeedBackService();

  const callFeedbackApi = (data) => {
    submitFeedback(data);
  };

  useEffect(() => {
    setGlobal((prev) => ({
      ...prev,
      appFormData: {
        ...prev.appFormData,
        healthDetailTab: healthDetailTab,
        redirectTo,
        lifeAssuredTab: lifeAssuredTab,
        currentFormStep: steps,
        payoutTab: payoutTab,
      },
    }));
  }, [healthDetailTab, steps, redirectTo, payoutTab, lifeAssuredTab]);

  const changePayoutDetailTab = (index) => {
    setPayoutTab(index);
  };

  const [spouseRedirectTo, setSpouseRedirectTo] = useState(false);

  const handleBack = async (trigger, saveData, formFields, redirectingFn, tab) => {
    //trigger returns true if all fields are valid(shouldfocus takes to first error field)
    const isValid = await trigger(undefined, { shouldFocus: true });

    if ((redirectTo && isValid) || !redirectTo) {
      saveData(formFields);

      setTimeout(
        () => {
          redirectingFn(tab);
        },
        tab === PAGE_STEPS.quoteScreen ? 500 : 0
      );
    }
  };

  const redirectToEdit = ({ redirectTo, tab, tabIndex }) => {
    setRedirectTo(redirectTo);

    if (isTdPersonaJourney) {
      if (tab === "Personal_Details") {
        changeLifeAssuredTab(tabIndex);
        setSteps({
          Personal_Details: CURRENT,
          Health_Details: UPCOMING,
          Pre_Confirmation: UPCOMING,
        });
      } else if (tab === "Health_Details") {
        changeHealthDetailTab(tabIndex);
        setSteps({
          Personal_Details: COMPLETE,
          Health_Details: CURRENT,
          Pre_Confirmation: UPCOMING,
        });
      }
    } else {
      if (tab === "life_assured") {
        changeLifeAssuredTab(tabIndex);
        setSteps((prevSteps) => ({
          ...prevSteps,
          life_assured_details: CURRENT,
          health_details: UPCOMING,
          ...(isProductIWish() ? { payout_details: UPCOMING } : {}),
          payment: UPCOMING,
        }));
      } else {
        changeHealthDetailTab(tabIndex);
        setSteps((prevSteps) => ({
          ...prevSteps,
          life_assured_details: COMPLETE,
          health_details: CURRENT,
          ...(isProductIWish() ? { payout_details: UPCOMING } : {}),
          payment: UPCOMING,
        }));
      }
    }
  };

  const redirectToReview = () => {
    setRedirectTo(false);
    if (isTdPersonaJourney) {
      setSteps({
        Personal_Details: COMPLETE,
        Health_Details: COMPLETE,
        Pre_Confirmation: CURRENT,
      });
    } else {
      setSteps((prevSteps) => ({
        ...prevSteps,
        ...(isIwishAndDependent(globalValues?.calculatorFormData) ? { your_details: COMPLETE } : {}),
        life_assured_details: COMPLETE,
        health_details: COMPLETE,
        ...(isProductIWish() ? { payout_details: COMPLETE } : {}),
        payment: CURRENT,
      }));
    }
  };

  const updateAppFormData = (data) => {
    if (currentApplication === APPLICATION_ONE) {
      setGlobal((prevGlobal) => ({
        ...prevGlobal,
        appFormData: {
          ...prevGlobal?.appFormData,
          ...data,
        },
      }));
    } else {
      setGlobal((prevGlobal) => ({
        ...prevGlobal,
        [currentApplication]: {
          ...prevGlobal?.[currentApplication],
          appFormData: {
            ...prevGlobal?.[currentApplication]?.appFormData,
            ...data,
          },
        },
      }));
    }
  };

  const updateMainApp = ({ key, data, otherData, stepsCleared }) => {
    setGlobal((prevGlobal) => ({
      ...prevGlobal,
      appFormData: {
        ...prevGlobal.appFormData,
        formWizard: {
          ...prevGlobal.appFormData.formWizard,
          [key]: { ...data },
        },
        stepsCleared: {
          ...prevGlobal.appFormData.stepsCleared,
          [key]: stepsCleared,
        },
      },
      ...otherData,
    }));
  };

  const updateSpouseApp = ({ key, data, otherData, stepsCleared }) => {
    setGlobal((prevGlobal) => ({
      ...prevGlobal,
      [currentApplication]: {
        ...prevGlobal[currentApplication],
        appFormData: {
          ...prevGlobal[currentApplication].appFormData,
          formWizard: {
            ...prevGlobal[currentApplication].appFormData.formWizard,
            [key]: { ...data },
          },
          stepsCleared: {
            ...prevGlobal[currentApplication].appFormData.stepsCleared,
            [key]: stepsCleared,
          },
        },
      },
      ...otherData,
    }));
  };

  const updateStepDataInContext = (funcProps) => {
    if (currentApplication === APPLICATION_ONE) {
      updateMainApp(funcProps);
    } else {
      updateSpouseApp(funcProps);
    }

    if (funcProps?.key !== "lastChangedField")
      setTimeout(() => {
        updateUserInput(callFeedbackApi);
      }, [500]);
  };

  const updateGlobalContextData = (data) => {
    setGlobal((prevGlobal) => ({
      ...prevGlobal,
      ...data,
    }));
  };

  const appData = {
    [APPLICATION_ONE]: {
      calculatorFormData: globalValues?.calculatorFormData,
      quoteFormData: globalValues?.quoteFormData,
      appFormData: globalValues?.appFormData,
      resumeDropOffJourney: globalValues?.resumeDropOffJourney,
    },
    [SPOUSE_COVER]: {
      calculatorFormData: globalValues?.[SPOUSE_COVER]?.calculatorFormData,
      quoteFormData: globalValues?.[SPOUSE_COVER]?.quoteFormData,
      appFormData: globalValues?.[SPOUSE_COVER]?.appFormData,
      resumeDropOffJourney: globalValues?.[SPOUSE_COVER]?.resumeDropOffJourney,
    },
  };
  // TODO: CHECK THIS BEFORE LIVE
  const isNorthStarFlow =
    //  false;
    isResumeJourney ? globalValues?.appFormData?.isNorthStarFlow : true;

  useEffect(() => {
    updateAppFormData({ isNorthStarFlow: isNorthStarFlow });
  }, [isNorthStarFlow]);

  useEffect(() => {
    if (!isResumeJourney && isProductIWish()) {
      setSteps(getNormalSteps());
    }
  }, [globalValues?.calculatorFormData?.lifeAssuredFor]);

  const values = {
    steps,
    setSteps,
    redirectToReview,
    changeLifeAssuredTab,
    changeProposerTab,
    proposerTab,
    lifeAssuredTab,
    changeHealthDetailTab,
    changePayoutDetailTab,
    payoutTab,
    healthDetailTab,
    redirectTo,
    setRedirectTo,
    spouseRedirectTo,
    setSpouseRedirectTo,
    redirectToEdit,
    handleBack,
    formRef,
    ...appData?.[currentApplication],
    updateAppFormData,
    updateGlobalContextData,
    updateStepDataInContext,
    currentApplication,
    isNorthStarFlow,
    isResumeJourney,
  };

  return <AppFormContext.Provider value={useMemo(() => values, [values])}>{children}</AppFormContext.Provider>;
};

export const useAppFormContext = () => {
  const context = useContext(AppFormContext);
  if (!context) {
    throw new Error("useAppFormContext must be used within a AppFormProvider");
  }
  return context;
};
